/**
 * Returns a deterministic background color for a recognition level ribbon
 * derived entirely from the tenant's marketplace theme colors.
 *
 * Palette is built from the 4 company colors (secondary, primary, accent,
 * neutral) plus darkened variants of each, giving 8 distinct theme-derived
 * options. White text contrast is ensured by clamping lightness.
 */

function hexToRgb(hex) {
  const h = (hex || '').replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16) || 0,
    parseInt(h.substring(2, 4), 16) || 0,
    parseInt(h.substring(4, 6), 16) || 0,
  ];
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;
}

function darken(hex, amount = 0.35) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

function ensureContrast(hex) {
  const [r, g, b] = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (luminance > 0.45) return darken(hex, 0.4);
  return hex;
}

export function getRecognitionRibbonColor(recognitionLevel, company) {
  const base = [
    company?.secondary_color || '#002949',
    company?.primary_color || '#5489B6',
    company?.accent_color || '#5B5E5A',
    company?.neutral_color || '#666666',
  ];

  const palette = [
    ...base.map(ensureContrast),
    ...base.map((c) => ensureContrast(darken(c, 0.3))),
  ];

  let hash = 0;
  const normalized = (recognitionLevel || '').trim().toLowerCase();
  for (let i = 0; i < normalized.length; i++) {
    hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
  }

  return palette[Math.abs(hash) % palette.length];
}
